import { ChangeDetectorRef, Component, HostListener, NgZone, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DELIVERY_SETTINGS, LeapV2BehaviouralConcern, LeapV2Session, LeapV2SessionRejection, LeapV2SessionReport, LeapV2SessionSaveResponse, formatSessionMoment } from './models/leap-v2-session.model';
import { LeapV2Student } from './models/leap-v2-student.model';
import {
  StudentAttendance,
  ABSENT_STATUSES,
  TutorCancelledDialogResult,
  ConsultantNotesDialogData,
  BehaviouralConcernDialogResult,
  SessionOutcome,
  resolveSessionOutcome,
} from './models/leap-v2-attendance.model';
import { LeapV2TutorCancelledDialogComponent } from './dialogs/leap-v2-tutor-cancelled-dialog/leap-v2-tutor-cancelled-dialog.component';
import { LeapV2ConsultantNotesDialogComponent } from './dialogs/leap-v2-consultant-notes-dialog/leap-v2-consultant-notes-dialog.component';
import { LeapV2DebugDialogComponent } from './dialogs/leap-v2-debug-dialog/leap-v2-debug-dialog.component';
import { TutorReportsHelperService } from '../../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { LeapV2SessionService } from './services/leap-v2-session.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PlacementService } from '../../../_services';
import { PlacementData } from '../../../_models/tutor-reports/tutorreport';
import { FeatureHelperService } from '../../../_helpers/feature-helper/feature-helper.service';
import { DynamicConfirmModalComponent, DynamicConfirmModalData } from '../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { SubmissionHistoryDialog } from '../../../shared/submission-history-dialog';
import { ErrorModalHelperService } from '../../../_helpers/error-modal-helper/error-modal-helper.service';
import { alignAttainmentLevels, isStudentProgressComplete } from './leap-v2-progress-validation';
import { ComponentCanDeactivate } from '../../../_guards';
import { hasRole, isInternalStaff } from '../../../_helpers/can-use/current-user-roles';
import { FeatureSwitchService } from '../../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../../_constants/feature-switch-keys';

@Component({
  selector: 'tutor-report-leap-v2',
  templateUrl: './tutor-report-leap-v2.component.html',
  styleUrls: ['./tutor-report-leap-v2.component.css'],
  standalone: false,
})
export class TutorReportLeapV2Component implements OnInit, ComponentCanDeactivate {
  // Populated by the load call in ngOnInit; the template guards on this being set.
  // Holds the full nested response ({sessionId, placementId, session, report, tutorCancellation, ...});
  // the getters below expose the individual nodes to the template and child inputs.
  sessionReport: LeapV2SessionReport;

  // Baselines for the unsaved-changes guard. Attendance and the report persist independently
  // (Submit attendance vs Save/Submit), so each has its own baseline, reset only when its own data is
  // persisted — see captureAttendanceBaseline / captureReportBaseline and hasUnsavedChanges().
  private attendanceBaseline = '';
  private reportBaseline = '';

  // Debug shows the raw model, so admins only.
  readonly canDebug = hasRole('ADMIN');
  // Consultant notes are the consultant's record of what they did about the register (story 35584): internal staff
  // read and write them, a tutor or client user does not. The API enforces the same ([StaffOnly]).
  readonly canUseConsultantNotes = isInternalStaff();
  readonly maxObjectives = 3;
  placementId: number;
  orbitSessionId: number;
  selectedSessionDate: Date = null;
  componentViewMode: any;
  // Brand the placement belongs to — drives the read-only badge next to the page title.
  brandName: string | null = null;
  // The per-session delivery setting (39883): shown, and required to finalise, only while its switch is on.
  deliverySettingOn = false;
  readonly deliverySettings = DELIVERY_SETTINGS;

  constructor(private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private tutorReportsHelperService: TutorReportsHelperService,
    private sessionService: LeapV2SessionService,
    private placementService: PlacementService,
    private featureHelperService: FeatureHelperService,
    private errorModalHelper: ErrorModalHelperService,
    // Optional so the unit specs that build the component by hand need not supply it: absent reads as off.
    private featureSwitches?: FeatureSwitchService,
  )
  {
    // Placement + Orbit session id come from the route (/add-session-v2/:id/:orbitSessionId) so the
    // screen survives refresh / deep links and addresses the exact session, not just a date
    // (SessionIdentityGrain.md §4 — a placement can have two sessions on one day).
    this.placementId = Number(this.route.snapshot.paramMap.get('id'));
    this.orbitSessionId = Number(this.route.snapshot.paramMap.get('orbitSessionId'));
    this.tutorReportsHelperService.ensureContext(this.placementId);

    // The finer nav state (session date + view mode) only exists on in-flow navigation; it's absent
    // on an external link / refresh, so guard the read rather than throwing. The date is no longer
    // load-bearing for identity (that's orbitSessionId above) but still drives display — see
    // seedDateForNewSession below.
    const insertedState = this.router.getCurrentNavigation()?.extras?.state;
    if (insertedState != null) {
      // Nav state carries a SelectedSessionDate {startDate, startTime, endTime}; only the date part is
      // needed here. Despite SelectedSessionDate.startDate being typed Date, the callers build it from a
      // raw JSON API row, so at runtime it is an ISO string — coerce it to a real Date or the downstream
      // date-pipe binding throws.
      const startDate = insertedState.selectedSessionDate?.startDate;
      this.selectedSessionDate = startDate ? new Date(startDate) : null;
      this.componentViewMode = insertedState.componentViewMode;
    }
  }

  ngOnInit(): void {
    // A booking that is not found or not the user's reads as one plain message, not the support modal (40973).
    this.sessionService.loadSession(this.placementId, this.orbitSessionId, true).subscribe({
      next: sessionReport => {
        this.sessionReport = sessionReport;
        // Identity comes from the route, not the GET body — stamp it so every save payload carries it.
        // Guarded: a failed load leaves sessionReport null (the template shows the loading state).
        if (this.sessionReport) {
          this.sessionReport.orbitSessionId = this.orbitSessionId;
          this.seedDateForNewSession();
          this.ensureAtLeastOneObjective();
          this.alignStudentAttainmentToObjectives();
          this.captureAttendanceBaseline();
          this.captureReportBaseline();
        }
        this.cdr.detectChanges();
      },
      error: error => this.errorModalHelper.show(error.message, error)
    });

    // (see reloadSession below — the session's id only exists once its rows do)

    this.featureSwitches?.isEnabled(FeatureSwitchKeys.sessionDeliverySetting).subscribe(on => {
      this.deliverySettingOn = on;
      this.cdr.detectChanges();
    });

    // This route is standalone (not nested under the TutorReportsComponent shell that
    // normally fetches PlacementData), so fetch the placement here for the brand badge.
    this.placementService.getById(this.placementId).subscribe({
      next: (response: PlacementData) => {
        this.brandName = response?.brandName ?? null;
        this.cdr.detectChanges();
      },
      error: error => this.errorModalHelper.show(error.message, error)
    });
  }

  // The whole screen's state lives on `sessionReport`; these getters keep the template
  // and child bindings reading from that single source of truth, exposing the individual
  // response nodes (session / report / tutorCancellation) the way the template expects.
  get session(): LeapV2Session {
    return this.sessionReport.session;
  }

  get students(): LeapV2Student[] {
    return this.sessionReport.report.students;
  }

  get objectives(): string[] {
    return this.sessionReport.report.objectives;
  }

  // A learning objective box must carry text — empty / whitespace-only boxes are not allowed.
  // Drives both the inline field error and the Submit gate so an empty objective can never be sent.
  isObjectiveEmpty(index: number): boolean {
    return (this.objectives[index] || '').trim().length === 0;
  }

  get objectivesValid(): boolean {
    return this.objectives.length > 0 && this.objectives.every(o => (o || '').trim().length > 0);
  }

  // Objectives describe what was taught, so they are only required while someone was there to be
  // taught. A tutor cancellation means no lesson ran at all (and the session is Cancelled — nothing is
  // submitted for it), and a register submitted with every learner absent means nobody was there to
  // teach — in both cases there is nothing to report against (the all-absent session is still submitted,
  // with no content, so the register can settle it). Before the register is submitted the requirement
  // stands, since a learner can still be switched back to Present. Drives the hint, the inline error and
  // the gate.
  get objectivesRequired(): boolean {
    if (this.tutorCancelled) return false;
    return !this.attendanceSubmitted
      || this.students.some(s => this.showStudentPanel(s.id));
  }

  // The report may only be submitted once it is complete: attendance submitted, at least one
  // objective (unless nobody attended — see objectivesRequired), and every attending student's
  // required fields filled in (attainment per objective, progress comments, engagement score,
  // engagement comments). Absent students have no panel (showStudentPanel) and no progress to
  // enter, so they are excluded. Evidence of work and behavioural concerns are optional and not
  // checked. A tutor cancellation is never complete: the cancellation itself cancels the session in
  // Orbit, so there is nothing left to submit. Drives the Submit button's disabled state and the
  // submitProgress() guard.
  get reportComplete(): boolean {
    if (this.tutorCancelled) return false;
    return this.attendanceSubmitted
      && (!this.objectivesRequired || this.objectivesValid)
      && (!this.deliverySettingRequired || !!this.sessionReport.report.deliverySetting)
      && this.students
        .filter(s => this.showStudentPanel(s.id))
        .every(s => isStudentProgressComplete(s.progress, this.objectives.length));
  }

  // Finalising needs the delivery setting while its switch is on (39883: "mandatory for session finalisation", not for
  // attendance). Every session is asked for one, including a session where every learner was cancelled with notice;
  // the API applies the same rule.
  get deliverySettingRequired(): boolean {
    return this.deliverySettingOn && !this.tutorCancelled;
  }

  get attendanceSubmitted(): boolean {
    return this.sessionReport.session.attendanceSubmitted;
  }

  get tutorCancelled(): boolean {
    return !!this.sessionReport.tutorCancellation;
  }

  // Attendance is locked once submitted or once the tutor cancelled: nothing in the register can change,
  // so the controls that would change it are hidden rather than shown disabled.
  get attendanceLocked(): boolean {
    return this.tutorCancelled || this.attendanceSubmitted;
  }

  // The report is read-only once it is finalised (Submitted, Approved or Cancelled) or the tutor cancelled: there is
  // no Save then, so every field is disabled rather than left editable (39889). Unlock is the way back; Open and
  // Rejected reports stay editable.
  get reportLocked(): boolean {
    return this.tutorCancelled || this.reportSubmitted || this.reportFinalised;
  }

  get tutorCancelledSummary(): TutorCancelledDialogResult | null {
    return this.sessionReport.tutorCancellation;
  }

  get anyStudentAttendanceSaved(): boolean {
    return this.sessionReport.session.attendance.length > 0;
  }

  // The report is final once submitted: the tutor's Save/Submit give way to the staff Accept/Reject.
  get reportSubmitted(): boolean {
    return this.session.status === 'Submitted';
  }

  // Approved and Cancelled are the terminal states: once authorised for payment, or cancelled, the footer must
  // offer no tutor Save/Submit and no staff Accept/Reject. Checked before reportSubmitted so a re-load of an
  // already-final session never re-shows them.
  //
  // 'Cancelled' is not a LeapDb StatusId — the GET derives it from the tutor-cancellation record, or from
  // Orbit's own status for a slot the coordinator cancelled in the diary. The cancellation record stays in the
  // test too: it is what the optimistic flip in openTutorCancelledDialog has to go on before the GET reloads.
  // A session Orbit cancelled on Submit (every learner gave 1+ days' notice) is NOT this — it has a submitted
  // report, reads Submitted and takes the normal Accept/Reject footer.
  get reportCancelled(): boolean {
    return this.session.status === 'Cancelled';
  }

  get reportFinalised(): boolean {
    return this.session.status === 'Approved' || this.reportCancelled || this.tutorCancelled;
  }

  // Rejected is NOT terminal: the report went back to the tutor, who corrects it and resubmits from the
  // same footer as an Open report. The banner above the form shows why it came back.
  get reportRejected(): boolean {
    return this.session.status === 'Rejected';
  }

  get rejection(): LeapV2SessionRejection | null {
    return this.sessionReport.rejection ?? null;
  }

  // Accept/Reject reuse the features that already govern the legacy tutor-report approval flow, so the
  // people who may approve a report are the people who may authorise its session for payment
  // (ADMIN/PRGMGR/TCMGR/TTRCRD — not tutors). The API enforces the same feature; this only hides UI.
  //
  // ⚠️ Do NOT gate this on `isStaff`. That flag means "holds the STAFF role code", and STAFF is the
  // TUTOR role here (UserService.GetTutorUsers filters tutors by it, and DigitalClassroom joins STAFF
  // users to a room as the tutor). Using it showed Accept to tutors and hid it from admins.
  get canApprove(): boolean {
    return this.featureHelperService.hasFeature('TutorReportsApprove');
  }

  get canReject(): boolean {
    return this.featureHelperService.hasFeature('TutorReportsReject');
  }

  // Drives the footer branch: the Accept/Reject row shows when the user can do either.
  get canAuthorise(): boolean {
    return this.canApprove || this.canReject;
  }

  // Submit is only allowed once every student has a saved attendance entry.
  // Entries requiring extra info (reason/comments/parent-carer) are only added
  // to session.attendance after the attendance dialog validates them, so a
  // present entry is always complete.
  get allStudentsHaveAttendance(): boolean {
    return this.students.length > 0 &&
      this.students.every(s => this.hasAttendance(s.id));
  }

  onAttendanceSaved(attendance: StudentAttendance): void {
    const others = this.session.attendance.filter(a => a.studentId !== attendance.studentId);
    this.session.attendance = [...others, attendance];
  }

  onAttendanceCleared(studentId: string): void {
    this.session.attendance = this.session.attendance.filter(a => a.studentId !== studentId);
  }

  removeObjective(index: number): void {
    if (this.objectives.length <= 1) return;
    this.sessionReport.report.objectives = this.objectives.filter((_, i) => i !== index);
    // See submitAttendance() — an unrelated CD error elsewhere in the app can
    // abort a global change-detection pass before it reaches this component.
    this.cdr.detectChanges();
  }

  addObjective(): void {
    if (this.objectives.length >= this.maxObjectives) return;
    this.sessionReport.report.objectives = [...this.objectives, ''];
    this.cdr.detectChanges();
  }

  openTutorCancelledDialog(): void {
    const ref = this.dialog.open(LeapV2TutorCancelledDialogComponent, {
      width: '440px',
      disableClose: true,
    });
      ref.afterClosed().subscribe((result: TutorCancelledDialogResult) => {
        if (result) {
          this.sessionReport.tutorCancellation = result;
          // Bug 37020. Two things were wrong here and both produced a 400.
          //
          // sessionReport.sessionId is the LeapDb anchor (MIN tblSessions.Id), which is 0 until the
          // session has rows. This button is disabled the moment any attendance is saved, so it is
          // ONLY ever pressed while that id is 0 — the API rejected every call the UI could make.
          // The cancellation is now keyed on the Orbit session, which the route always carries.
          //
          // bookingId was hardcoded null on the strength of the GET not exposing one. It does not
          // need to: the placement id IS the booking id here (the API writes it to
          // tblSessionTutorCancellation.PlacementId), and the route carries that too.
          //
          // The API also cancels the session in Orbit (the tutor is not paid), so on success the session is
          // Cancelled — terminal, and the status the GET returns for it from now on — and on failure
          // (422 = Orbit refused, with a message) the optimistic cancellation is rolled back so the register
          // stays editable.
          this.sessionService
            .tutorCancellation(this.placementId, this.orbitSessionId, result.reason, result.comments)
            .subscribe({
              next: () => {
                this.ngZone.run(() => {
                  this.session.status = 'Cancelled';
                  this.cdr.detectChanges();
                });
              },
              error: (error) => {
                this.sessionReport.tutorCancellation = null;
                this.errorModalHelper.show(error.message, error);
                this.cdr.detectChanges();
              },
            });
        }
      });
  }

  openDebugDialog(): void {
    this.dialog.open(LeapV2DebugDialogComponent, {
      width: '600px',
      data: { title: 'session model', model: this.sessionReport },
    });
  }

  /**
   * Consultant notes on a submitted register. Saved by their own call the moment the dialog closes (39886): they used
   * to be held only on the page, so a reload lost them. The page adopts the notes only once the API has stored them.
   */
  openConsultantNotesDialog(): void {
    const data: ConsultantNotesDialogData = { existingNotes: this.session.consultantNotes };
    this.dialog.open(LeapV2ConsultantNotesDialogComponent, { width: '440px', data })
      .afterClosed()
      .subscribe((result: { notes: string }) => {
        if (result == null) return;
        this.sessionService.saveConsultantNotes(this.placementId, this.orbitSessionId, result.notes).subscribe({
          next: () => {
            this.ngZone.run(() => {
              this.session.consultantNotes = result.notes;
              this.session.notesLastUpdated = result.notes ? new Date() : null;
              this.cdr.detectChanges();
            });
          },
          // 422 when the register is not submitted; nothing was saved, so say so.
          error: (error) => this.errorModalHelper.show(error.message, error),
        });
      });
  }

  // Confirmation body for Finalise session, by what the saved register means for the session in Orbit
  // (resolveSessionOutcome). Every outcome is an Orbit write that cannot be undone from this screen, so
  // all three confirm first. AwaitReport = Authorised with learning delivered, so it alone locks the report.
  private static readonly FINALISE_CONFIRM: { [outcome in SessionOutcome]: string[] } = {
    AwaitReport: [
      'This completes the session. Your session report can no longer be changed.',
      'If you spot a mistake later, contact your consultant',
    ],
    Authorised: [
      'This completes the session and you will not be able to change it.',
      'If you spot a mistake later, contact your consultant',
    ],
    Cancelled: [
      'As this session did not take place, there is nothing further to do. You will not be able to change it.',
      'If you spot a mistake later, contact your consultant',
    ],
  };

  // Shown to internal staff after a submit the server answered OrbitUnchanged: an earlier submit already set the
  // session's Orbit status (D29), or payroll already held a session the register cancels (D27), so Orbit was left
  // as it is.
  private static readonly ORBIT_UNCHANGED_NOTICE: DynamicConfirmModalData = {
    title: 'Orbit not changed',
    paragraphs: [
      'The session is finalised. Its status in Orbit has not been changed.',
      'If the attendance change affects pay or bill, the consultant adjusts the session in Orbit.',
    ],
    confirmLabel: 'OK',
    hideCancel: true,
  };

  // The tutor's notice for the same outcome. Tutor-facing text never mentions pay, payroll or Orbit, and none of
  // that is the tutor's to act on, so this is a plain success message (product owner's wording, 39887).
  private static readonly FINALISED_NOTICE: DynamicConfirmModalData = {
    title: 'Session finalised',
    paragraphs: ['This completes this session report and no further actions are required.'],
    confirmLabel: 'OK',
    hideCancel: true,
  };

  /**
   * Submit attendance (Form 1). Saves and locks the register — nothing is settled here. Whatever the
   * attendance values, the tutor then presses Finalise session on the report, and that is where the register
   * decides pay & bill (see submitProgress). The status therefore stays Open and needs no confirmation.
   */
  submitAttendance(): void {
    this.sessionService.submitAttendance(this.sessionReport).subscribe({
      next: (response: LeapV2SessionSaveResponse) => {
        this.ngZone.run(() => {
          this.session.attendanceSubmitted = true;
          this.session.status = response.status;
          // A pre-existing, unrelated change-detection error elsewhere in the app
          // (FooterComponent) can abort a global CD pass before it reaches this
          // component. Force this component's own view to refresh regardless.
          this.cdr.detectChanges();
          // The attendance submit does not persist objectives / progress (see submitAttendance in the
          // service), so a full reload would blank the report the tutor may already be editing — keep it
          // and adopt only the server-owned identity/session fields (the new sessionId in particular).
          this.reloadSession(true);
        });
      },
      error: (error) => {
        // 422 carries a user-facing message; 500/502 leaves the attendance saved and the status
        // unchanged — either way the tutor needs to see it and retry.
        this.errorModalHelper.show(error.message, error);
      },
    });
  }

  /**
   * Re-fetches the session after a write that may have created it.
   *
   * A session that does not exist yet has no `dbo.tblSessions` rows, so the page-load GET returns
   * `sessionId = 0` — the id is `MIN(tblSessions.Id)` of the placement+date group, which only exists
   * once the first save/submit has written those rows. Without this refetch the component kept the
   * `0` from page load for the lifetime of the visit, and every id-keyed call made afterwards
   * (Accept, Reject/Unlock, tutor cancellation, behavioural concern) posted `sessionId: 0` and was
   * refused with "A valid sessionId is required". This `sessionId` (the LeapDb anchor row id) is a
   * different id from `orbitSessionId` — the latter never changes for this visit.
   *
   * Deliberately silent: it repairs state, and the write it follows has already succeeded, so a
   * failure here must not surface as an error for an action that worked.
   */
  private reloadSession(preserveReport = false): void {
    // When preserving, hold the report node (objectives + per-student progress) the tutor is editing so
    // the refetch cannot overwrite unsaved work — only the server-owned identity/session fields are adopted.
    const editedReport = preserveReport ? this.sessionReport?.report ?? null : null;
    this.sessionService.loadSession(this.placementId, this.orbitSessionId).subscribe(sessionReport => {
      if (!sessionReport) return;
      if (editedReport) {
        sessionReport.report = editedReport;
      }
      this.sessionReport = sessionReport;
      this.sessionReport.orbitSessionId = this.orbitSessionId;
      this.seedDateForNewSession();
      this.ensureAtLeastOneObjective();
      this.alignStudentAttainmentToObjectives();
      // Attendance is now server truth after any reload. The report is only server truth on a full
      // replace (submitProgress); a preserved report is the tutor's still-unsaved work, so its baseline
      // must stay put or navigating away would not prompt.
      this.captureAttendanceBaseline();
      if (!preserveReport) {
        this.captureReportBaseline();
      }
      this.cdr.detectChanges();
    });
  }

  /**
   * A brand-new session (sessionId 0 — see reloadSession above) has no persisted Date, and the GET no
   * longer echoes one either: identity moved to orbitSessionId (SessionIdentityGrain.md §4), so there is
   * no date input left for the API to echo back. The nav-state date (still carried for display, per the
   * constructor) is the only source left, so seed it here for the meta card and anything else reading
   * `session.date` before the first save persists a real one.
   *
   * `report.date` needs the same seed, not just `session.date`: the two are separate Date references
   * (see LeapV2SessionService.mapResponse — report.date falls back to the *pre-seed* sessionNode.date at
   * map time, so reassigning session.date here does not touch it). Form 2's Save/Submit buttons appear as
   * soon as submitAttendance() flips attendanceSubmitted synchronously — before the reloadSession() it
   * also triggers has resolved — so a user can hit Save/Submit in that window while report.date is still
   * unseeded. Without this, that click POSTs date: 0001-01-01 to the Report endpoint.
   */
  private seedDateForNewSession(): void {
    if (this.sessionReport?.sessionId === 0 && this.selectedSessionDate) {
      if (this.sessionReport.session) {
        this.sessionReport.session.date = this.selectedSessionDate;
      }
      if (this.sessionReport.report) {
        this.sessionReport.report.date = this.selectedSessionDate;
      }
    }
  }

  /**
   * A session with no persisted objectives comes back with an empty array, which renders no objective
   * box at all — only an "Add objective" button — so the "at least one objective is required" rule is
   * invisible. Seed a single empty objective so its input (and inline required error) shows up front.
   * Seeded before the dirty baseline is captured, so the empty box is not treated as an unsaved change.
   */
  private ensureAtLeastOneObjective(): void {
    const objectives = this.sessionReport?.report?.objectives;
    if (objectives && objectives.length === 0) {
      objectives.push('');
    }
  }

  /**
   * The student panel pads each student's attainmentLevels to the objective count on load (it holds one
   * score per objective). That padding runs in the child's ngOnInit — after this component captures its
   * report baseline — so without matching it here first, the pad reads as an unsaved change and the leave
   * prompt fires on an untouched form. Align to the same shape (after ensureAtLeastOneObjective) so the
   * baseline reflects it. Uses the objective count, so this must run once objectives are settled.
   */
  private alignStudentAttainmentToObjectives(): void {
    const objectiveCount = this.sessionReport?.report?.objectives?.length ?? 0;
    for (const student of this.sessionReport?.report?.students ?? []) {
      alignAttainmentLevels(student.progress, objectiveCount);
    }
  }

  /**
   * Finalise session — the tutor's one final action, whatever the register holds. The API applies the
   * attendance rule to the saved register (SessionAuthorisation.md §5.3a, D14/D15): every learner cancelled
   * with 1+ days notice → the session is cancelled in Orbit (tutor not paid); anything else → marked payable
   * in Orbit. Either way LEAP goes to Submitted and staff Accept/Reject as normal (D16). Both are Orbit writes
   * that cannot be undone from this screen (Reject and Unlock leave them be), so it confirms first with wording
   * for what is about to happen — resolveSessionOutcome only picks the wording; the status in the response
   * is what the page adopts.
   */
  submitProgress(): void {
    // Defence in depth: the Finalise session button is already disabled while the report is incomplete, but
    // never send a report that fails validation even if that guard is bypassed.
    if (!this.reportComplete) {
      this.cdr.detectChanges();
      return;
    }
    const outcome = resolveSessionOutcome(this.session.attendance.map(a => a.status), this.tutorCancelled);
    const moment = formatSessionMoment(this.session.date, this.session.startTime);
    const confirmData: DynamicConfirmModalData = {
      title: moment ? `Finalise session for ${moment}` : 'Finalise session',
      paragraphs: TutorReportLeapV2Component.FINALISE_CONFIRM[outcome],
      confirmLabel: 'Finalise session',
      cancelLabel: 'Continue editing',
    };
    this.dialog.open(DynamicConfirmModalComponent, { width: '440px', data: confirmData })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;

        this.sessionService.submitProgress(this.sessionReport).subscribe({
          next: (response: LeapV2SessionSaveResponse) => {
            this.ngZone.run(() => {
              // Submit settles the session in Orbit; LEAP's status (Submitted) comes from the response.
              this.session.status = response.status;
              this.cdr.detectChanges();
              // Submit is what creates the session's rows on a first-time session, so this is the refetch
              // that gives Accept/Reject a real sessionId to post.
              this.reloadSession();
              if (response.outcome === 'OrbitUnchanged') {
                this.dialog.open(DynamicConfirmModalComponent, {
                  width: '440px',
                  data: isInternalStaff()
                    ? TutorReportLeapV2Component.ORBIT_UNCHANGED_NOTICE
                    : TutorReportLeapV2Component.FINALISED_NOTICE,
                });
              }
            });
          },
          error: (error) => {
            // An Orbit failure (500/502) lands here with the report content already saved and the status
            // unchanged — the tutor needs to see it so they press Submit again.
            this.errorModalHelper.show(error.message, error);
          },
        });
      });
  }

  saveSession(): void {
    this.sessionService.saveSession(this.sessionReport).subscribe({
      next: () => {
        // Draft persisted — the report is now saved, so it is no longer dirty.
        this.captureReportBaseline();
        this.cdr.detectChanges();
      },
      // A refused save (422, e.g. a file that could not be read) is not shown by the error interceptor, and
      // nothing was saved, so the tutor has to be told.
      error: (error) => this.errorModalHelper.show(error.message, error),
    });
  }

  /**
   * Unlock attendance — reopens the submitted register (and with it returns the session to Open). Only the
   * register's button calls this; the session report's Unlock is reopenSession.
   */
  unlockAttendance(): void {
    this.sessionService.unlockAttendance(this.sessionReport.placementId, this.sessionReport.sessionId).subscribe({
      next: () => {
        this.ngZone.run(() => {
          this.session.attendanceSubmitted = false;
          // Server-driven flag change, not user edits — re-baseline so it isn't read as unsaved.
          this.captureAttendanceBaseline();
          this.cdr.detectChanges();
          // Unlock also resets the status to Open. Poking attendanceSubmitted alone left a Submitted
          // session still showing Accept/Reject over a row the server had already reopened, so Accept
          // then 422'd; refetch and let the server's status drive the footer.
          this.reloadSession();
        });
      },
      error: (error) => {
        // 422 when the register is not submitted (nothing to unlock).
        this.errorModalHelper.show(error.message, error);
      },
    });
  }

  /**
   * The session report's Unlock — reopens a submitted or approved report so the consultant, or the tutor they
   * tell, can correct its content and resubmit with Save and Finalise session. Leaves the register submitted,
   * so unlike Unlock attendance the report stays on screen and the resubmit needs no re-registering. The Orbit
   * payment mark is not revoked. Staff only (UnlockSession), so the confirmation may mention Orbit.
   */
  reopenSession(): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: {
        title: 'Unlock session report',
        confirmLabel: 'Unlock',
        confirmMessage: 'Unlock this session report? It becomes editable again by you and the tutor until it is finalised again. Any sign-off is cleared; payment already authorised in Orbit is unaffected.',
      },
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;

        this.sessionService.reopenSession(this.sessionReport.placementId, this.sessionReport.sessionId)
          .subscribe({
            next: () => {
              this.ngZone.run(() => {
                this.session.status = 'Open';
                this.cdr.detectChanges();
                this.reloadSession();
              });
            },
            error: (error) => {
              this.errorModalHelper.show(error.message, error);
            },
          });
      });
  }

  /**
   * Accept — signs the report off in LEAP (StatusId=Approved). LEAP-only: payment was authorised in
   * Orbit when the tutor submitted (see submitProgress), so nothing here touches Orbit.
   */
  acceptSession(): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: 'Accept this session report? Payment was already authorised when the tutor submitted it; this records your sign-off.' },
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;

        this.sessionService.authoriseSession(this.sessionReport.placementId, this.sessionReport.sessionId)
          .subscribe({
            next: () => {
              this.ngZone.run(() => {
                this.session.status = 'Approved';
                this.cdr.detectChanges();
              });
            },
            error: (error) => {
              this.errorModalHelper.show(error.message, error);
            },
          });
      });
  }

  /**
   * Reject — sends the report back to the tutor with a mandatory reason. The session becomes Rejected
   * (red pill + banner, counted on the placement tile), the reason is recorded, and the tutor alone is
   * emailed. Attendance is left as submitted: it is the report that came back.
   *
   * Reuses the legacy SubmissionHistoryDialog (20–200 chars). Its Cancel closes with `true`, so only the
   * onConfirmed emit — never afterClosed() — counts as a confirmation (same wiring as ReportControlsComponent).
   */
  rejectSession(): void {
    const dialogRef = this.dialog.open(SubmissionHistoryDialog, {
      panelClass: 'my-dialog',
      data: { title: 'Reject session report', label: 'Reason for rejection (emailed to the tutor)' },
    });
    const confirmed = dialogRef.componentInstance.onConfirmed.subscribe((reason: string) => {
      dialogRef.close();
      this.sessionService.rejectSession(this.sessionReport.placementId, this.sessionReport.sessionId, reason)
        .subscribe({
          next: () => {
            this.ngZone.run(() => {
              this.session.status = 'Rejected';
              // Optimistic; reloadSession() replaces it with the server's record (who / when).
              this.sessionReport.rejection = { reason, rejectedBy: '', rejectedDate: new Date() };
              this.cdr.detectChanges();
              this.reloadSession();
            });
          },
          error: (error) => {
            this.errorModalHelper.show(error.message, error);
          },
        });
    });
    dialogRef.afterClosed().subscribe(() => confirmed.unsubscribe());
  }

  isStudentAbsent(studentId: string): boolean {
    const att = this.session.attendance.find(a => a.studentId === studentId);
    return att ? ABSENT_STATUSES.includes(att.status) : false;
  }

  hasAttendance(studentId: string): boolean {
    return this.session.attendance.some(a => a.studentId === studentId);
  }

  // The persisted attendance entry for a student, so the attendance card can render the saved status and
  // its reason/comments on load — not just when set live this visit. Returns the same object reference
  // across change-detection passes until session.attendance changes, so it doesn't churn the card's input.
  attendanceFor(studentId: string): StudentAttendance | null {
    return this.session.attendance.find(a => a.studentId === studentId) ?? null;
  }

  showStudentPanel(studentId: string): boolean {
    return !this.hasAttendance(studentId) || !this.isStudentAbsent(studentId);
  }

  // --- Behavioural concerns -------------------------------------------------------------------------
  // One per student per session, server-owned (dbo.tblSessionBehaviouralConcern, insert-only). Raised
  // as its own POST the moment the dialog closes — NOT carried on the report Save/Submit, whose request
  // DTO has no concern field and silently dropped it. The list on sessionReport is refreshed from the
  // GET after each raise, so it never enters the unsaved-changes snapshots.

  // The concern already raised for a student, or null. The API returns the list oldest-first; if more
  // than one ever exists for a student (nothing in the API forbids it) the first raised is the one shown.
  concernFor(studentId: string): LeapV2BehaviouralConcern | null {
    return this.sessionReport.behaviouralConcerns.find(c => String(c.studentId) === studentId) ?? null;
  }

  // The POST needs the LeapDb anchor id (MIN tblSessions.Id), which is 0 until the attendance submit's
  // silent reload lands — so the button stays disabled in that window rather than posting sessionId: 0.
  // Approved and a tutor cancellation are terminal for the whole screen, concerns included.
  get canRaiseConcern(): boolean {
    return !this.tutorCancelled && !this.reportFinalised && this.sessionReport.sessionId > 0;
  }

  onConcernRaised(studentId: string, result: BehaviouralConcernDialogResult): void {
    this.sessionService
      .submitBehaviouralConcern(Number(studentId), this.placementId, this.sessionReport.sessionId, result.reason, result.comments)
      .subscribe(() => {
        // Adopt the stored concern from server truth while keeping the tutor's unsaved report edits.
        this.ngZone.run(() => this.reloadSession(true));
      });
  }

  // --- Unsaved-changes guarding ---------------------------------------------------------------------
  // The whole screen is a form held in memory on `sessionReport`; it is only persisted by Submit
  // attendance / Save / Submit. These snapshots capture the editable state (not server ids) so we can
  // detect edits the tutor would lose by navigating away.

  private snapshotAttendance(): string {
    const session = this.sessionReport?.session;
    return JSON.stringify({
      attendance: session?.attendance ?? [],
      attendanceSubmitted: session?.attendanceSubmitted ?? false,
    });
  }

  private snapshotReport(): string {
    const report = this.sessionReport?.report;
    return JSON.stringify({
      objectives: report?.objectives ?? [],
      progress: (report?.students ?? []).map(s => s.progress),
    });
  }

  private captureAttendanceBaseline(): void {
    this.attendanceBaseline = this.snapshotAttendance();
  }

  private captureReportBaseline(): void {
    this.reportBaseline = this.snapshotReport();
  }

  hasUnsavedChanges(): boolean {
    if (!this.sessionReport) return false;
    return this.snapshotAttendance() !== this.attendanceBaseline
      || this.snapshotReport() !== this.reportBaseline;
  }

  // CanDeactivate hook (UnsavedChangesGuard delegates here). Confirm = leave; Cancel/backdrop = stay.
  canDeactivate(): boolean | Observable<boolean> {
    if (!this.hasUnsavedChanges()) return true;
    return this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: 'You have unsaved changes on this session. Leave without saving?' },
    }).afterClosed().pipe(map(confirmed => confirmed === true));
  }

  // Refresh / tab-close: ask the browser to show its native "leave site?" prompt while dirty.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
}
